// PostHog analytics — ported from the original index.html init (L24-25) and the
// scattered `window.posthog?.capture(...)` calls. All helpers no-op until init()
// runs and only when a key is configured, so they're safe to call from anywhere.
import type posthog from 'posthog-js';

type PostHogType = typeof posthog;
let started = false;
let scheduled = false;
let posthogClient: PostHogType | null = null;
const eventQueue: Array<{
  type: 'capture' | 'identify' | 'reset';
  event?: string;
  props?: Record<string, unknown>;
  id?: string;
}> = [];

export async function initAnalytics(): Promise<void> {
  if (started) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return; // no key configured (e.g. local dev) → stay a no-op
  started = true;
  try {
    const { default: ph } = await import('posthog-js');
    ph.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
      defaults: '2026-01-30',
      capture_performance: { web_vitals: true },
    } as Parameters<PostHogType['init']>[1]);
    posthogClient = ph;

    for (const item of eventQueue) {
      if (item.type === 'capture' && item.event) {
        ph.capture(item.event, item.props);
      } else if (item.type === 'identify' && item.id) {
        ph.identify(item.id, item.props);
      } else if (item.type === 'reset') {
        ph.reset();
      }
    }
    eventQueue.length = 0;

    // Global error reporting (original L3184-3185).
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (e) => {
        if (e.error) ph.captureException(e.error);
      });
      window.addEventListener('unhandledrejection', (e) => {
        if (e.reason) ph.captureException(e.reason);
      });
    }
  } catch (err) {
    console.warn('Failed to load PostHog analytics:', err);
  }
}

/**
 * Move analytics loading out of the critical render path. Two animation frames
 * ensure the app has had a chance to paint; requestIdleCallback then yields to
 * any immediate interaction. Calls made before PostHog is ready remain in the
 * existing queue and are replayed by initAnalytics().
 */
export function scheduleAnalytics(): void {
  if (scheduled || started || typeof window === 'undefined') return;
  if (!import.meta.env.VITE_POSTHOG_KEY) return;
  scheduled = true;

  const start = () => void initAnalytics();
  const runWhenIdle = () => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    };

    if (idleWindow.requestIdleCallback) {
      idleWindow.requestIdleCallback(start, { timeout: 2_000 });
    } else {
      window.setTimeout(start, 0);
    }
  };

  if (typeof window.requestAnimationFrame !== 'function') {
    runWhenIdle();
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(runWhenIdle);
  });
}

export function capture(event: string, props?: Record<string, unknown>): void {
  if (posthogClient) {
    posthogClient.capture(event, props);
  } else if (!started || (started && !posthogClient)) {
    eventQueue.push({ type: 'capture', event, props });
  }
}

export function identifyUser(id: string, props?: Record<string, unknown>): void {
  if (posthogClient) {
    posthogClient.identify(id, props);
  } else {
    eventQueue.push({ type: 'identify', id, props });
  }
}

export function resetUser(): void {
  if (posthogClient) {
    posthogClient.reset();
  } else {
    eventQueue.push({ type: 'reset' });
  }
}
